import React, { FC, useEffect, useRef, useState } from "react";
import { HotTable } from "@handsontable/react";
import { CellChange } from "handsontable/common";
import { ImageScale, ITableComponent, ITableComponentCell, SWTypes } from "interfaces/sw/SWInterfaces";
import { Settings as GetMergedCellSettings } from "handsontable/plugins/mergeCells";
import { DetailedSettings as ContextMenuSettings } from "handsontable/plugins/contextMenu";
import { CellRange } from "handsontable";
import CustomCellEditorComponent from "./CustomCellEditorComponent";
import './EnhancedTableEditor.scoped.scss';
import "handsontable/dist/handsontable.full.min.css"
import { useDispatch } from "react-redux";
import { getTableCellImageUrls, setTableLabel, updateTable, updateTableWidths, } from "store/manageSW/manageSWActions";
import { AddTimeImageTargets, ImageDataDestinations, } from "store/manageSW/manageSWTypes";
import useSelector from "store/useSelector";
import MergeCellsPopUpModal from "./MergeCellsPopUpModal";
import Handsontable from 'handsontable';
import ChangeEnhancedTableCellsFontPopUpModal from "./ChangeEnhancedTableCellsFontPopUpModal";
import { cloneDeep } from "lodash";
import { showErrorToast } from "store/toast/toastActions";
export interface ITableEditorProps {
  tableComponent: ITableComponent,
  allowEdit: boolean,
  stepGuid: string,
  swGuid: string,
  swVersion: number,
  imageDataDestination: ImageDataDestinations,
  swType: SWTypes,
  stepId?: number,
  rcGuid?: string,
  rcVersion?: number,
  rcContainerStepId?: number,
  masterStepId?: number,
}

const EnhancedTableEditor: FC<ITableEditorProps> = ({
  tableComponent,
  allowEdit,
  stepGuid,
  swGuid,
  swVersion,
  imageDataDestination,
  swType,
  stepId,
  rcGuid,
  rcVersion,
  rcContainerStepId,
  masterStepId
}) => {
  const maxRowCount: number = useSelector(store => store.manageSW.maxRowCountForTableComponent);
  const maxColCount: number = 10;
  const dispatch = useDispatch();
  const { timeImageData } = useSelector(store => store.manageSW);
  const hotTableRef = useRef<any>(null);
  const [colWidths, setColWidths] = useState<number[]>([]);
  const [displayMergePopUp, setDisplayMergePopUp] = useState<boolean>(false);
  const mergePopUpRef = useRef<boolean>(false);
  const userMergeInProgressRef = useRef<boolean>(false);
  const pendingRowRemovalsRef = useRef<Array<{ index: number, amount: number }>>([]);
  const rowRemovalFlushScheduledRef = useRef<boolean>(false);
  const pendingColRemovalsRef = useRef<Array<{ index: number, amount: number }>>([]);
  const colRemovalFlushScheduledRef = useRef<boolean>(false);
  let imageChange: boolean = false;
  let fontChange: boolean = false;
  const [displayFontSizeChangePopUp, setDisplayFontSizeChangePopUp] = useState<boolean>(false);
  const setCells = (newCells: ITableComponentCell[], newColWidths: number[]) => {
    dispatch(
      updateTable({
        component: { ...tableComponent, cells: newCells, colWidths: newColWidths },
        stepGuid,
        stepId,
        rcContainerStepId,
        masterStepId
      })
    );
  };

  const handleImageUpload = (fileName: string, newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      cell.imageFileName = fileName;
      cell.scale = scaleOption;
      setCells(newCells, colWidths);
    }
  };

  const handleImageScaleUpdate = (newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      cell.scale = scaleOption;
      setCells(newCells, colWidths);
    }
  };

  const updateCellAfterDeleteImg = (newHtmlContent: string, rowIndex: number, colIndex: number) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      setCells(newCells, colWidths);
    }
  };

  const setNewColumnWidths = (isDirty: boolean) => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const instanceWidth = hotInstance?.rootElement?.offsetWidth;
    if (instanceWidth) {
      const columns = hotInstance.countSourceCols();
      const newWidth = instanceWidth / columns;
      setColWidths(new Array(columns).fill(newWidth));
      dispatch(updateTableWidths({
        component: { ...tableComponent },
        stepGuid: stepGuid,
        widths: new Array(columns).fill(newWidth),
        isDirty: isDirty
      }))
      return new Array(columns).fill(newWidth);
    }
    return [];
  }

  useEffect(() => {
    let newCells: ITableComponentCell[] = [];
    let rows: number = 0;
    let cols: number = 0;
    let newColWidths = [];

    if (tableComponent.cells.length === 0) {
      newColWidths = setNewColumnWidths(false)
    } else if (tableComponent.colWidths !== undefined) {
      setColWidths(tableComponent.colWidths)
      newColWidths = tableComponent.colWidths;
    } else {
      newColWidths = setNewColumnWidths(false);
    }

    var colCount = tableComponent.cells.length === 0 ? tableComponent.colCount : Math.max(...tableComponent.cells.map(o => o.colIndex)) + 1;
    var rowCount = tableComponent.cells.length === 0 ? tableComponent.rowCount : Math.max(...tableComponent.cells.map(o => o.rowIndex)) + 1;

    while (rows < rowCount) {
      cols = 0;
      while (cols < colCount) {
        let cell: ITableComponentCell[] = tableComponent.cells.filter(c => c.rowIndex === rows && c.colIndex === cols);
        if (cell[0] === undefined) {
          newCells.push({
            colIndex: cols,
            rowIndex: rows,
            colSpan: 1,
            rowSpan: 1,
            value: ""
          })
          cols++;
        } else {
          newCells.push(cell[0]);
          cols = cols + cell[0].colSpan
        }
      }
      rows++;
    }
    setCells(newCells, newColWidths)
  }, [])

  useEffect(() => {
    if (!hotTableRef.current) {
      return;
    }

    const newColCount: number = hotTableRef.current.hotInstance.countSourceCols();
    const newRowcount: number = hotTableRef.current.hotInstance.countSourceRows();

    if (
      newColCount !== tableComponent.colCount ||
      newRowcount !== tableComponent.rowCount
    ) {
      dispatch(
        updateTable({
          component: {
            ...tableComponent,
            rowCount: newRowcount,
            colCount: newColCount,
          },
          stepGuid,
        })
      );
    }
  }, [
    hotTableRef.current,
    hotTableRef.current?.hotInstance.countSourceCols(),
    hotTableRef.current?.hotInstance.countSourceRows(),
  ]);

  useEffect(() => {
    for (const cell of tableComponent.cells) {
      const imgSrc = getImage(cell);

      if (imgSrc) {
        dispatch(
          getTableCellImageUrls({
            stepGuid,
            componentGuid: tableComponent.guid,
            rowIndex: cell.rowIndex,
            colIndex: cell.colIndex,
            swGuid,
            swVersion,
            filename: imgSrc,
            destination: imageDataDestination,
            rcGuid,
            rcVersion,
            rcContainerStepId,
            masterStepId
          })
        );
      }
    }
  }, [tableComponent.cells]);

  const getMergedCells = (): GetMergedCellSettings => {
    return {
      virtualized: true,
      cells: tableComponent.cells
        .filter((x) => x.colSpan > 1 || x.rowSpan > 1)
        .map((x) => ({
          row: x.rowIndex,
          col: x.colIndex,
          rowspan: x.rowSpan,
          colspan: x.colSpan
        }))
    };

  };

  useEffect(() => {
    if (!hotTableRef.current) {
      return;
    }

    const hot = hotTableRef.current?.hotInstance;
    hot.batch(() => {
      tableComponent.cells.forEach((x: any) => {
        const physicalRow = hot.toPhysicalRow(x.rowIndex);
        const physicalCol = hot.toPhysicalColumn(x.colIndex);

        if (
          Number.isInteger(physicalRow) &&
          physicalRow >= 0 &&
          Number.isInteger(physicalCol) &&
          physicalCol >= 0
        ) {
          hot.setSourceDataAtCell(physicalRow, physicalCol, x.value);
          hot.setCellMeta(x.rowIndex, x.colIndex, 'scale', x.scale);
        }
      });
    });

    setTimeout(() => {
      hot.render();
    }, 100);
  }, [tableComponent.cells, hotTableRef.current]);

  const handleCellChanges = (changes: CellChange[] | null) => {
    if (!changes) {
      return;
    }

    let anyChanges = false;
    const newCells = tableComponent.cells.map((x) => ({ ...x }));

    changes.forEach(([row, col, oldValue, newValue]) => {
      if (oldValue !== newValue) {
        anyChanges = true;
      }

      let cell = newCells.find((x) => x.rowIndex === row && x.colIndex === col);
      const normalizedValue =
        typeof newValue === "string"
          ? normalizeSupportedInlineStyles(newValue)
          : newValue;

      if (cell) {
        cell.value = normalizedValue;
        if (cell.scale === undefined && normalizedValue !== null && normalizedValue !== "" && normalizedValue !== undefined) {
          const scaleMatch = String(normalizedValue).match(/data-scale=["']([^"']+)["']/i);
          if (scaleMatch?.[1] !== undefined) {
            cell.scale = scaleMatch[1] as ImageScale;
          }
        }
      } else if (typeof row === "number" && typeof col === "number") {
        cell = {
          rowIndex: row,
          colIndex: col,
          value: normalizedValue,
          colSpan: 1,
          rowSpan: 1,
        };
        newCells.push(cell);
      }
    });

    if (anyChanges) {
      const newWidths = updateColWidths();
      setColWidths(newWidths)
      setCells(newCells, newWidths);
    }
  };

  const contextMenuSettings: ContextMenuSettings = {
    items: {
      row_above: {
        disabled: !allowEdit,
      },
      row_below: {
        disabled: !allowEdit,
      },
      col_left: {
        disabled: !allowEdit,
      },
      col_right: {
        disabled: !allowEdit,
      },
      remove_row: {
        disabled: !allowEdit,
      },
      remove_col: {
        disabled: !allowEdit,
      },
      cut: {
        disabled: !allowEdit,
      },
      copy: {
        disabled: !allowEdit,
      },
      pasteMessage: {
        name: 'To paste press Ctrl + V',
        disabled: true,
        disableSelection: true,
        isCommand: false
      },
      mergeCells: {
        disabled: !allowEdit,
      },
      changeFontSize: {
        name() {
          return 'Change Font Size for table';
        },
      }
    },
    callback: function (key) {
      if (key === 'changeFontSize') {
        setDisplayFontSizeChangePopUp(true);
      }
    },
  };

  const afterMergeCells = (cellRange: CellRange, _mergeParent: unknown, auto: boolean) => {
    if (auto || !userMergeInProgressRef.current) {
      userMergeInProgressRef.current = false;
      return;
    }

    const { to, from, highlight } = cellRange;

    if (mergePopUpRef.current) {
      setDisplayMergePopUp(true)
    }
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    const highlightCell = newCells.find(
      (x) => x.rowIndex === highlight.row && x.colIndex === highlight.col
    );

    if (!highlightCell) {
      userMergeInProgressRef.current = false;
      return;
    }

    newCells = newCells.filter(
      (x) =>
        x.rowIndex < from.row ||
        x.rowIndex > to.row ||
        x.colIndex < from.col ||
        x.colIndex > to.col
    );

    const expectedRowSpan = to.row - from.row + 1;
    const expectedColSpan = to.col - from.col + 1;

    if (
      highlightCell.rowSpan === expectedRowSpan &&
      highlightCell.colSpan === expectedColSpan
    ) {
      userMergeInProgressRef.current = false;
      return;
    }

    highlightCell.rowSpan = expectedRowSpan;
    highlightCell.colSpan = expectedColSpan;
    highlightCell.rowIndex = from.row;
    highlightCell.colIndex = from.col;
    newCells.push(highlightCell);
    setCells(newCells, colWidths);
    userMergeInProgressRef.current = false;
  };

  const afterUnmergeCells = ({ from }: CellRange) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    const firstCell = newCells.find(
      (x) => x.rowIndex === from.row && x.colIndex === from.col
    );

    if (!firstCell) {
      return;
    }

    firstCell.rowSpan = 1;
    firstCell.colSpan = 1;
    setCells(newCells, colWidths);
  };

  const afterCreateCol = (index: number, amount: number) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    newCells = newCells.map((x) => {
      if (x.colIndex >= index) {
        x.colIndex += amount;
      }
      return x;
    });


    for (let i = 0; i < hotTableRef.current.hotInstance.countSourceRows(); i++) {
      const newCell: ITableComponentCell = {
        colIndex: index,
        colSpan: 1,
        rowIndex: i,
        rowSpan: 1,
        value: ""
      }

      newCells.sort((a, b) => {
        return a.colIndex - b.colIndex
      })

      newCells.push(newCell)
    }


    let newColWidths = updateColWidths()
    setCells(newCells, newColWidths);
  };

  const afterCreateRow = (index: number, amount: number) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    newCells = newCells.map((x) => {
      if (x.rowIndex >= index) {
        x.rowIndex += amount;
      }
      return x;
    });

    for (let i = 0; i < hotTableRef.current.hotInstance.countSourceCols(); i++) {
      const newCell: ITableComponentCell = {
        colIndex: i,
        colSpan: 1,
        rowIndex: index,
        rowSpan: 1,
        value: ""
      }

      newCells.sort((a, b) => {
        return a.rowIndex - b.rowIndex
      })

      newCells.push(newCell)
    }
    setCells(newCells, colWidths);
  };

  const afterRemoveCol = (index: number, amount: number) => {
    pendingColRemovalsRef.current.push({ index, amount });

    if (colRemovalFlushScheduledRef.current) {
      return;
    }

    colRemovalFlushScheduledRef.current = true;

    Promise.resolve().then(() => {
      colRemovalFlushScheduledRef.current = false;

      const removals = pendingColRemovalsRef.current.splice(0);
      let newCells = tableComponent.cells.map((cell) => ({ ...cell }));

      removals.forEach(({ index: removalIndex, amount: removalAmount }) => {
        const endIndex = removalIndex + removalAmount;

        newCells = newCells
          .filter(
            (cell) => cell.colIndex < removalIndex || cell.colIndex >= endIndex
          )
          .map((cell) =>
            cell.colIndex >= endIndex
              ? { ...cell, colIndex: cell.colIndex - removalAmount }
              : cell
          );
      });

      const newColWidths = updateColWidths();
      setCells(newCells, newColWidths);
    });
  };

  const afterRemoveRow = (index: number, amount: number) => {
    pendingRowRemovalsRef.current.push({ index, amount });

    if (rowRemovalFlushScheduledRef.current) {
      return;
    }

    rowRemovalFlushScheduledRef.current = true;

    Promise.resolve().then(() => {
      rowRemovalFlushScheduledRef.current = false;

      const removals = pendingRowRemovalsRef.current.splice(0);
      let newCells = tableComponent.cells.map((cell) => ({ ...cell }));

      removals.forEach(({ index: removalIndex, amount: removalAmount }) => {
        const endIndex = removalIndex + removalAmount;

        newCells = newCells
          .filter(
            (cell) => cell.rowIndex < removalIndex || cell.rowIndex >= endIndex
          )
          .map((cell) =>
            cell.rowIndex >= endIndex
              ? { ...cell, rowIndex: cell.rowIndex - removalAmount }
              : cell
          );
      });

      setCells(newCells, colWidths);
    });
  };

  const beforeMergeCells = (cellRange: CellRange, auto: boolean) => {
    if (auto || !allowEdit) {
      userMergeInProgressRef.current = false;
      return;
    }

    userMergeInProgressRef.current = true;

    const hot = hotTableRef.current?.hotInstance;
    if (hot && !mergePopUpRef.current) {
      const { from, to } = cellRange;

      for (let row = from.row; row <= to.row; row++) {
        for (let col = from.col; col <= to.col; col++) {
          const value = hot.getDataAtCell(row, col);
          if (value !== null && value !== "" && (from.row !== row || from.col !== col)) {
            mergePopUpRef.current = true
            break;
          }
        }
      }

    }
  }

  const updateColWidths = () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (hotInstance) {
      const newWidths = []
      for (let i = 0; i < hotInstance.countSourceCols(); i++) {
        newWidths.push(hotInstance.getColWidth(i))
      }
      setColWidths(newWidths)
      dispatch(updateTableWidths({
        component: { ...tableComponent },
        stepGuid: stepGuid,
        widths: newWidths,
        isDirty: true
      }))
      return newWidths;
    }

    return [];
  }

  let onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setTableLabel({
      stepGuid,
      componenGuid: tableComponent.guid,
      label: e.target.value,
    }))
  }

  const handleBeforeCopy = (data: any, coords: any) => {
    for (let row = 0; row < data.length; row++) {
      for (let col = 0; col < data[row].length; col++) {
        data[row][col] = cleanHtmlTags(data[row][col])
      }
    }
  }

  const cleanHtmlTags = (html: any) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    let result: any[] = [];

    for (const child of Array.from(div.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;
        if (element.tagName === 'P') {
          result.push(element.innerHTML?.trim())
        } else if (element.tagName === "UL" || element.tagName === "OL") {
          result.push(element.outerHTML?.trim())
        } else if (element.tagName === 'IMG') {
          result.push(element.outerHTML.replace("\"=\"\"", "/"))
        } else {
          result.push(element.textContent || element.outerHTML || '')
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent?.trim();
        if (text) {
          result.push(text)
        }
      }
    }

    return result.join('\n');
  }

  function escapeHtml(text: string): string {
    return text
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  const handleBeforePaste = (changes: any[][], coords: any[]) => {
    if (changes.length + coords[0].startRow > maxRowCount) {
      dispatch(showErrorToast(`Exceeded maximum row count of ${maxRowCount}.`));
      return false;
    }

    if (changes[0].length + coords[0].startCol > maxColCount) {
      dispatch(showErrorToast(`Exceeded maximum column count of ${maxColCount}.`));
      return false;
    }

    for (let i = 0; i < changes.length; i++) {
      for (let j = 0; j < changes[i].length; j++) {
        let cellContent = changes[i][j];
        let wrapped = '';
        // Convert value to string if needed
        if (cellContent == null) {
          wrapped = '';
        } else {
          wrapped = String(cellContent);
        }

        // Preserve <img ...> tags, encode all other < and >
        // Replace <img ...> tags with a placeholder
        const imgRegex = /<img[^>]*>/gi;
        let imgTags: string[] = [];
        wrapped = wrapped.replace(imgRegex, function (match) {
          imgTags.push(match);
          return `__IMG_PLACEHOLDER_${imgTags.length - 1}__`;
        });
        // Encode all < and >
        wrapped = wrapped.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        // Restore <img ...> tags from placeholders
        imgTags.forEach((img, idx) => {
          wrapped = wrapped.replace(`__IMG_PLACEHOLDER_${idx}__`, img);
        });
        wrapped = wrapped
          .replace(/&lt;(\/?strong)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?b)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?em)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?i)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?u)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ins)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?li\b[^&]*?)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ul)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ol)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?span\b[^&]*?)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?a\b[^&]*?)&gt;/gi, "<$1>")

        let parts = wrapped.split(/\n+/).filter(p => p.trim() !== "");
        wrapped = parts.map(p => addPTagForTextsOnly(p)).join("");
        wrapped = selfCloseImgTag(wrapped)
        // Wrap with <p>...</p> so only these tags are preserved
        changes[i][j] = wrapped;
      }
    }
  }

  const addPTagForTextsOnly = (element: string) => {
    if (element.includes("<img") || element.includes("<ul") || element.includes("<ol") || element.includes("<li")) {
      return `${element}`
    } else {
      return `<p>${element}</p>`
    }
  }

  function selfCloseImgTag(html: string) {
    return html.replace(/<img\b([^>]*)>/gi, (m) => {
      if (m.endsWith("/>")) return m;

      return m.replace(/>$/, " />")
    })
  }

  function imageOnlyRenderer(
    instance: any,
    td: HTMLElement,
    row: number,
    col: number,
    prop: any,
    value: any,
    cellProperties: any
  ) {
    // Clear existing content
    Handsontable.dom.empty(td);

    if (typeof value === 'string' && value.includes('<img')) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = value;

      // Only update <img> tags
      const images = wrapper.querySelectorAll('img');
      images.forEach((img) => {
        const scale = cellProperties.scale;
        let maxWidth = '100px';

        switch (scale) {
          case 'Small':
            maxWidth = '50px';
            break;
          case 'Medium':
            maxWidth = '100px';
            break;
          case 'Large':
            maxWidth = '150px';
            break;
        }

        const imageClass = scale === 'Small' ? 'small-image' : scale === 'Large' ? 'large-image' : 'medium-image';
        img.className = imageClass;
        img.style.maxWidth = maxWidth;
        img.style.height = 'auto';
        img.style.display = 'block';
        img.style.margin = '5px 0';
      });

      // Inject back the whole content (text + styled image)
      Array.from(wrapper.childNodes).forEach((node) => td.appendChild(node));
    }

    return td;
  }

  const handleFontSizeChange = (fontSize: number) => {
    fontChange = true;
    const hotInstance = hotTableRef.current?.hotInstance.getData();

    if (!!!hotInstance) {
      alert('Cells must be selected');
      return;
    }

    if (!!!fontSize || isNaN(fontSize)) {
      alert('Invalid font size');
      return;
    }

    let newCells = cloneDeep(tableComponent.cells.map((x) => ({ ...x })));

    newCells.forEach(cell => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cell.value, 'text/html');
      const body = doc.body;
      const validTags = ['P', 'SPAN', 'LI', 'EM', 'STRONG', 'U', 'INS'];

      const updateFontSize = (element: HTMLElement) => {
        element.style.fontSize = `${fontSize}px`;
      };

      // Processes list items.
      const processListItems = (liElement: HTMLElement) => {
        const hasSpan = liElement.querySelector('span');
        const hasDirectText = Array.from(liElement.childNodes).some(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
        );

        if (hasDirectText && !hasSpan) {
          liElement.innerHTML = liElement.innerHTML.replace(
            /(^|>)([^<]+)(?=<|$)/g,
            `$1<span style="font-size: ${fontSize}px;">$2</span>`
          );
        } else if (hasSpan) {
          const span = liElement.querySelector('span') as HTMLElement;
          updateFontSize(span);
        }

        liElement.querySelectorAll('em, strong, u, ins').forEach((nestedElement) => {
          const parentSpan = nestedElement.closest('span') as HTMLElement;
          if (!parentSpan) {
            const wrapperSpan = document.createElement('span');
            wrapperSpan.style.fontSize = `${fontSize}px`;
            nestedElement.parentNode?.replaceChild(wrapperSpan, nestedElement);
            wrapperSpan.appendChild(nestedElement);
          } else {
            updateFontSize(parentSpan);
          }
        });

        liElement.style.fontSize = '';
      };

      // Set the font size for all elements. List item gets an special treatment
      body.querySelectorAll('*').forEach((node) => {
        if (validTags.includes(node.tagName)) {
          if (node.tagName !== 'LI') {
            updateFontSize(node as HTMLElement);
          }
          else {
            processListItems(node as HTMLElement);
          }
        }
      });

      // Set content for invalid tag in cell
      if (!body.querySelector(validTags.map(tag => tag.toLowerCase()).join(','))) {
        body.innerHTML = `<p><span style="font-size: ${fontSize}px;">${cell.value}</span></p>`;
      }

      // update the cell value with new content
      cell.value = normalizeSupportedInlineStyles(body.innerHTML);
    });

    setCells(newCells, colWidths);
  }

  return (
    <div>
      <label className="mandatory controlLabel">
        Label
      </label>
      <input
        type="text"
        className="controlWide"
        value={tableComponent.label}
        onChange={(e) => onLabelChange(e)}
        disabled={!allowEdit}
      />
      <br />
      <br />
      <div style={{ width: "100%", overflow: "auto" }}>
        <HotTable
          ref={hotTableRef}
          colHeaders={() => ""}
          rowHeaders={() => ""}
          height="auto"
          width={"100%"}
          colWidths={colWidths}
          licenseKey="ceed5-f5fb4-6ef45-64847-ffe1b"
          renderer={"html"}
          afterChange={(changes) => {
            if (imageChange) {
              imageChange = false;
              return;
            } else if (fontChange) {
              fontChange = false;
              return;
            }
            else {
              handleCellChanges(changes);
            }
          }}
          mergeCells={getMergedCells()}
          contextMenu={contextMenuSettings}
          afterMergeCells={afterMergeCells}
          afterUnmergeCells={afterUnmergeCells}
          afterCreateCol={afterCreateCol}
          afterCreateRow={afterCreateRow}
          afterRemoveCol={afterRemoveCol}
          afterRemoveRow={afterRemoveRow}
          afterColumnResize={updateColWidths}
          minRows={1}
          minCols={1}
          maxRows={maxRowCount}
          maxCols={maxColCount}
          manualColumnResize={allowEdit}
          manualRowResize={allowEdit}
          startCols={tableComponent.cells.length === 0 ? tableComponent.colCount : Math.max(...tableComponent.cells.map(o => o.colIndex)) + 1}
          startRows={tableComponent.cells.length === 0 ? tableComponent.rowCount : Math.max(...tableComponent.cells.map(o => o.rowIndex)) + 1}
          readOnly={!allowEdit}
          stretchH="all"
          autoColumnSize={false}
          autoRowSize={false}
          beforePaste={handleBeforePaste}
          beforeCopy={handleBeforeCopy}
          beforeCut={handleBeforeCopy}
          beforeMergeCells={beforeMergeCells}
        >
          <CustomCellEditorComponent
            hot-editor={true}
            hotTableRef={hotTableRef}
            handleImageUpload={handleImageUpload}
            handleImageScaleUpdate={handleImageScaleUpdate}
            disabled={!allowEdit}
            timeImageData={timeImageData}
            enableTimeUpload={true}
            swType={swType}
            swGuid={swGuid}
            swVersion={swVersion}
            componentGuid={tableComponent.guid}
            stepGuid={stepGuid}
            timeImageTarget={AddTimeImageTargets.ManageSW}
            updateCellAfterDeleteImg={updateCellAfterDeleteImg}
          />
        </HotTable>
      </div>
      {displayMergePopUp &&
        <MergeCellsPopUpModal
          onClose={() => {
            mergePopUpRef.current = false;
            setDisplayMergePopUp(false)
          }}
        />
      }
      {displayFontSizeChangePopUp &&
        <ChangeEnhancedTableCellsFontPopUpModal
          onClose={() => {
            setDisplayFontSizeChangePopUp(false);
          }}
          onChangeFont={(fontSize) => {
            handleFontSizeChange(fontSize);
            setDisplayFontSizeChangePopUp(false);
          }}
          fontSize={11}
        />
      }
    </div>
  );
};

export default EnhancedTableEditor;

const normalizeSupportedInlineStyles = (html: string): string => {
  if (!html) {
    return html;
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  container.querySelectorAll<HTMLElement>("[style]").forEach((element) => {
    const styleAttribute = element.getAttribute("style");
    if (!styleAttribute) {
      return;
    }

    const declarations = styleAttribute
      .split(";")
      .map((declaration) => declaration.trim())
      .filter((declaration) => declaration.length > 0);

    let fontSizeValue: string | undefined;
    let fontFamilyValue: string | undefined;
    let textAlignValue: string | undefined;
    const otherDeclarations: Array<{ property: string; value: string }> = [];

    declarations.forEach((declaration) => {
      const separatorIndex = declaration.indexOf(":");
      if (separatorIndex < 0) {
        return;
      }

      const property = declaration.substring(0, separatorIndex).trim().toLowerCase();
      const value = declaration.substring(separatorIndex + 1).trim();

      if (!value) {
        return;
      }

      switch (property) {
        case "font-size":
          fontSizeValue = value;
          break;
        case "font-family":
          fontFamilyValue = value.replace(/["']/g, "");
          break;
        case "text-align":
          textAlignValue = value;
          break;
        default:
          otherDeclarations.push({ property, value });
          break;
      }
    });

    const normalizedDeclarations: string[] = [];

    if (fontSizeValue) {
      normalizedDeclarations.push(`font-size: ${fontSizeValue};`);
    }

    if (fontFamilyValue) {
      normalizedDeclarations.push(`font-family: ${fontFamilyValue};`);
    }

    if (textAlignValue) {
      normalizedDeclarations.push(`text-align: ${textAlignValue};`);
    }

    otherDeclarations.forEach(({ property, value }) => {
      normalizedDeclarations.push(`${property}: ${value};`);
    });

    if (normalizedDeclarations.length === 0) {
      element.removeAttribute("style");
      return;
    }

    element.setAttribute("style", normalizedDeclarations.join(" "));
  });

  return container.innerHTML;
};

const getImage = (cell: ITableComponentCell): string | undefined => {
  if (
    cell.imageFileName &&
    cell.imageFileName.includes("blob:https://")
  ) {
    return undefined;
  }

  const div = document.createElement("div");
  div.innerHTML = normalizeSupportedInlineStyles(cell.value);
  const imgTag = div.getElementsByTagName("img")[0];

  if (imgTag && imgTag.getAttribute("alt") !== "undefined") {
    return imgTag.getAttribute("alt") || undefined;
  }

  return undefined;
};
